// FLAGS: none
// The language and session-type dropdowns and the "only mine" switch, shared by the list and the calendar. Languages
// show by name (SessionLanguage.<code>), sorted by it; the URL keeps the code.
import { useTranslation } from "react-i18next";
import {
  HostCalendarState,
  SESSION_TYPES,
  sessionTypeLabelKey,
} from "@src/views/HostSignups/calendar/hostCalendar";

type FilterState = Pick<HostCalendarState, "language" | "type" | "mine">;

const selectClass =
  "rounded-lg border-2 border-slate-200 bg-white px-3 py-1.5 text-sm text-indigo-800 focus:border-green-500 focus:outline-none";
const labelClass =
  "text-xs font-semibold uppercase tracking-wide text-slate-700";

interface HostFilterControlsProps {
  state: FilterState;
  languages: string[];
  onChange: (partial: Partial<FilterState>) => void;
}

const HostFilterControls = ({
  state,
  languages,
  onChange,
}: HostFilterControlsProps) => {
  const { t } = useTranslation();
  // A code without a translation shows as itself rather than as its key.
  const languageOptions = languages
    .map((code) => ({
      code,
      name: t(`SessionLanguage.${code}`, { defaultValue: code }),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="flex flex-wrap items-center gap-4">
      <label className="flex items-center gap-2">
        <span className={labelClass}>
          {t("HostSignups.Calendar.Filter.Language")}
        </span>
        <select
          className={selectClass}
          value={state.language ?? ""}
          onChange={(event) =>
            onChange({ language: event.target.value || null })
          }
        >
          <option value="">{t("HostSignups.Calendar.Filter.All")}</option>
          {languageOptions.map(({ code, name }) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-2">
        <span className={labelClass}>
          {t("HostSignups.Calendar.Filter.Type")}
        </span>
        <select
          className={selectClass}
          value={state.type ?? ""}
          onChange={(event) =>
            onChange({
              type:
                SESSION_TYPES.find((type) => type === event.target.value) ??
                null,
            })
          }
        >
          <option value="">{t("HostSignups.Calendar.Filter.All")}</option>
          {SESSION_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(sessionTypeLabelKey[type])}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-2 text-sm text-indigo-800">
        <input
          type="checkbox"
          className="h-4 w-4 accent-green-600"
          checked={state.mine}
          onChange={(event) => onChange({ mine: event.target.checked })}
        />
        {t("HostSignups.Calendar.Filter.OnlyMine")}
      </label>
    </div>
  );
};

export default HostFilterControls;
